// dependencies
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { nanoid } from "nanoid/non-secure";
import React, { ReactNode, useMemo, useRef } from "react";

import { faGripDotsVertical, faPencil } from "@fortawesome/pro-regular-svg-icons";
import { faClone, faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import { ButtonStandard, PageBanner } from "@performyard/py-components";

//components
import Icon from "../../../../../../components/Icon/Icon";
import Spacer from "../../../../../../components/Spacer";

//utils
import { ReviewQuestion, ReviewQuestionSchema, normalizeQuestions } from "../../utils";

//store
import { useEditFormStore } from "../../useEditFormStore";
import CalculatorEdit from "../EditQuestionBlock/questions/calculator";
import GoalDiscussionEdit from "../EditQuestionBlock/questions/goal-discussion";
import LegacyRatingScaleEdit from "../EditQuestionBlock/questions/legacyRatingScale";
import MultipleChoiceEdit from "../EditQuestionBlock/questions/multiple-choice";
import NumericEdit from "../EditQuestionBlock/questions/numeric";
import CalculatorQuestion from "./questions/calculator";

export type QuestionTypeMap = {
	date: ReviewQuestion;
	text: ReviewQuestion;
	numeric: ReviewQuestion;
};

export type EditQuestionBlockProps = {
	question: ReviewQuestion;
	index: number;
};

export const questionDisplayMap: {
	[key: string]: {
		label: string;
		edit: (props: EditQuestionBlockProps) => ReactNode;
		preview: (data: ReviewQuestion) => ReactNode;
	};
} = {
	date: { label: "Date", edit: () => null, preview: () => null },
	text: { label: "Long Answer", edit: () => null, preview: () => null },
	multiline: { label: "Multiple Line Answer", edit: () => null, preview: () => null },
	numeric: {
		label: "Numeric Answer",
		edit: () => <NumericEdit />,
		preview: () => null,
	},
	"rating-scale": {
		label: "Rating Scale",
		edit: (props: EditQuestionBlockProps) => <LegacyRatingScaleEdit {...props} />,
		preview: () => null,
	},
	"multiple-choice": {
		label: "Multiple Choice",
		edit: (props: EditQuestionBlockProps) => <MultipleChoiceEdit {...props} />,
		preview: () => null,
	},
	"multiple-select": {
		label: "Multiple Choice",
		edit: (props: EditQuestionBlockProps) => <MultipleChoiceEdit {...props} />,
		preview: () => null,
	},
	"goal-creation": { label: "Goal Creation", edit: () => null, preview: () => null },
	"goal-discussion": {
		label: "Goal Discussion",
		edit: (props: EditQuestionBlockProps) => <GoalDiscussionEdit {...props} />,
		preview: () => null,
	},
	instructions: {
		label: "Instructions",
		edit: () => null,
		preview: () => null,
	},
	calculator: {
		label: "Numeric Calculation",
		edit: (props: EditQuestionBlockProps) => <CalculatorEdit {...props} />,
		preview: (data: ReviewQuestion) => <CalculatorQuestion data={data} />,
	},
};

type QuestionBlockProps = {
	refId: string;
	index: number;
	onRequestEdit: (refId: string) => void;
};

const QuestionBlock = ({ index, refId, onRequestEdit }: QuestionBlockProps) => {
	const questionRef = useRef<HTMLDivElement>(null);

	const { questions, addQuestion, deleteQuestion } = useEditFormStore((state) => ({
		editingQuestion: state.editingQuestion,
		questions: state.questions,
		addQuestion: state.addQuestion,
		deleteQuestion: state.deleteQuestion,
	}));

	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: refId });

	const question = questions.find((q) => q.refId === refId);

	const invalidMessage = useMemo(() => {
		if (!question) return undefined;
		const result = ReviewQuestionSchema.safeParse(question);
		if (!result.success) {
			return result.error.errors[0]?.message;
		}
		return undefined;
	}, [question]);

	if (!question) return null;

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			id={`question-block-${index}`}
			className={`mb-4 grid grid-cols-[24px_1fr] justify-center border-[1px] border-solid border-neutral-2 bg-neutral-0 ${invalidMessage ? "border-red-6" : "border-neutral-2"}`}
		>
			<div
				{...listeners}
				className="flex w-6 cursor-grab items-center justify-center border-r-[1px] border-solid border-neutral-2 text-neutral-7 hover:text-neutral-9 active:cursor-grabbing"
			>
				<Icon icon={faGripDotsVertical} />
			</div>

			<div ref={questionRef} className="max-w-[calc(100% - 24px)] group min-w-0 break-words p-4">
				<div className="mb-2 flex h-8 items-center justify-between">
					<p className={`text-paragraph-3 font-semi-bold ${invalidMessage ? "text-red-6" : ""}`}>
						{questionDisplayMap[question.type]?.label}
						{question.type === "multiple-select" && (
							<span className="font-normal text-neutral-6"> (Supports Multiple Selections)</span>
						)}
					</p>
					<div className="hidden gap-2 group-hover:flex group-focus:flex">
						<ButtonStandard
							data-testid="edit-question"
							color="neutral"
							size="medium"
							iconLeft={faPencil}
							variant="outline"
							id={`submit-edit-question-${refId}`}
							onClick={() => onRequestEdit(refId)}
						>
							Edit
						</ButtonStandard>
						<ButtonStandard
							type="button"
							data-testid="duplicate-question"
							color="neutral"
							size="medium"
							iconLeft={faClone}
							variant="outline"
							id={`duplicate-question-${refId}`}
							onClick={() => {
								const newRefId = nanoid();
								const newQuestion = { ...question, refId: newRefId };
								const normalizedNewQuestion = normalizeQuestions([newQuestion])[0];
								addQuestion(normalizedNewQuestion, index + 1);
							}}
						>
							Duplicate
						</ButtonStandard>
						<ButtonStandard
							type="button"
							data-testid="delete-question"
							color="red"
							size="medium"
							onClick={() => {
								deleteQuestion(refId);
							}}
						>
							Delete
						</ButtonStandard>
					</div>
				</div>
				<h4 className="max-w-full overflow-hidden text-wrap break-words text-paragraph-1 font-semi-bold">
					{question.question}
					{question.is_required ? (
						<span data-testid="isRequiredQuestion" className="text-primary-5">
							{" "}
							*
						</span>
					) : null}
				</h4>
				{question.subtext ? <div className="is-markdown whitespace-pre-wrap">{question.subtext}</div> : null}
				{invalidMessage ? (
					<PageBanner color="red" size="small" icon={faExclamationTriangle} mainTitle={invalidMessage} />
				) : null}
				{questionDisplayMap[question.type]?.preview(question) !== null ? (
					<div>{questionDisplayMap[question.type]?.preview(question)}</div>
				) : !invalidMessage ? (
					<Spacer height={2} />
				) : null}
			</div>
		</div>
	);
};

export default QuestionBlock;
